/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App, Spin } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import React from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Button from 'src/components/ts-components/Button'
import { usePlayerProfile } from 'src/features/profile/hooks/usePlayerProfile'
import { useMePlayerProfile } from 'src/features/profile/player/setupModal/hooks/useMePlayerProfile'
import { useFinderSettings } from './hooks/useFinderSettings'
import { useFinderOptIn } from './hooks/useFinderOptIn'
import { useFinderPreferenceActions } from './hooks/useFinderPreferenceActions'
import OptInHeaderCard from './components/OptInHeaderCard'
import FinderPreferenceGrid from './components/FinderPreferenceGrid'

interface Props {
  playerId: string
}

const PlayerFinderTab: React.FC<Props> = ({ playerId }) => {
  const { message } = App.useApp()
  const { data, isLoading } = useFinderSettings(playerId)
  const { data: player } = usePlayerProfile(playerId)
  const genderId = player?.gender_id ?? null
  // SN-8295 — each card has its own location; an Add's starts from the
  // profile address (the Edit Player form's own record of it) when complete.
  const { data: profile, loading: profileLoading } =
    useMePlayerProfile(playerId)

  const optIn = useFinderOptIn(playerId)
  const isOptedIn = data?.is_opted_in ?? false
  const preferences = data?.preferences ?? []

  const { openAdd, handleEdit, handleRemove, modals } =
    useFinderPreferenceActions(playerId, {
      preferences,
      genderId,
      profileAddress: profile?.address,
    })

  const handleToggle = (next: boolean) => {
    // Opting in with no preferences: BE requires at least one, so open the Add
    // modal instead. Creating the first preference opts the player in
    // server-side; the settings refetch then flips the toggle ON. Cancelling
    // leaves it OFF (bound to server state).
    if (next && preferences.length === 0) {
      openAdd()
      return
    }
    optIn.mutate(
      { isOptedIn: next },
      { onError: (e) => message.error(e.message) }
    )
  }

  // The profile seeds an Add's location, so the form waits for it too.
  if (isLoading || (profileLoading && !profile)) {
    return (
      <div css={loadingStyle}>
        <Spin />
      </div>
    )
  }

  return (
    <div css={containerStyle}>
      <OptInHeaderCard
        value={isOptedIn}
        onChange={handleToggle}
        disabled={optIn.isPending}
      />
      {isOptedIn ? (
        <>
          <FinderPreferenceGrid
            preferences={preferences}
            onEdit={handleEdit}
            onRemove={handleRemove}
          />
          <div css={addRowStyle}>
            <Button
              text={capitalizeWords('Add Profile')}
              variant='primary'
              onClick={openAdd}
              icon={<PlusOutlined />}
              style={addButtonStyle}
            />
          </div>
        </>
      ) : null}
      {modals}
    </div>
  )
}

export default PlayerFinderTab

// ─── Styles ────────────────────────────────────────────────────────────────

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`

const loadingStyle = css`
  display: flex;
  justify-content: center;
  padding: 48px;
`

// Left-align the Add action and let it size to its content (the shared Button
// is `flex: 1 0 0`, which would otherwise stretch it full-width on desktop).
const addRowStyle = css`
  display: flex;
  justify-content: flex-start;
`

const addButtonStyle = css`
  flex: 0 0 auto;
`
